import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isUniqueRetailerName,
  isValidPercentageDecimal,
  isValidPostCode,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingFarmPrimaryData,
  useGetFootprintingFarmPurchases,
  useGetFootprintingFarmRetailers,
  useMutateFootprintingFarmPrimaryData,
  useMutateFootprintingFarmResourcePurchases,
  useTranslation,
} from "utils";

import { TRANSPORT_COUNTRY_CODE } from "consts";

import { RetailerData, RetailerPurchase } from "models";

import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  Icon,
  Input,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Row,
  Select,
  Space,
  Tooltip,
} from "components";

interface FarmTransportProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

export const FarmTransportForm = ({ primaryDataId, readOnly, farmId, year }: FarmTransportProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);

  const [purchases, setPurchases] = useState<RetailerPurchase[]>([]);
  const [purchasesActive, setPurchasesActive] = useState<boolean | null>(null);

  const retailersQuery = useGetFootprintingFarmRetailers(farmId);
  const farmPrimaryDataQuery = useGetFootprintingFarmPrimaryData(farmId, year);

  const purchasesQuery = useGetFootprintingFarmPurchases(primaryDataId ?? "");

  const { mutate: mutateFarmResourcePurchases } = useMutateFootprintingFarmResourcePurchases(
    farmId,
    year,
    {
      farmId,
      footprintingFarmPrimaryDataId: primaryDataId || "",
      purchases: purchases.map((p) => ({
        retailer: p.retailer,
        ...(p.retailerId ? { retailerId: p.retailerId } : {}),
        // Set all null fields to `0` when saving
        mineralFertilizerPct: p.mineralFertilizerPct ?? 0,
        organicFertilizerPct: p.organicFertilizerPct ?? 0,
        plantProtectionPct: p.plantProtectionPct ?? 0,
        seedsPct: p.seedsPct ?? 0,
        cowFeedPct: p.cowFeedPct ?? (carbonProgramQuery.data?.platformFeatures.footprinting?.dairy ? 0 : null),
      })),
    },
    primaryDataId
  );

  const { mutate: mutateFarmPrimaryData } = useMutateFootprintingFarmPrimaryData(
    farmId,
    year,
    {
      purchasesActive,
      farmId,
      year,
    },
    primaryDataId
  );

  const hardSetFormValues = (ps: RetailerPurchase[]) => {
    if (!formRef.current) return;

    formRef.current.resetFields();
    ps.forEach((purchase, i) => {
      formRef.current?.setFieldsValue({
        [`retailer.name-${i}`]: purchase.retailer.name,
        [`retailer.countryCode-${i}`]: purchase.retailer.countryCode,
        [`retailer.postcode-${i}`]: purchase.retailer.postcode,
        [`mineralFertilizerPct-${i}`]: (purchase.mineralFertilizerPct ?? 0) * 100,
        [`organicFertilizerPct-${i}`]: (purchase.organicFertilizerPct ?? 0) * 100,
        [`seedsPct-${i}`]: (purchase.seedsPct ?? 0) * 100,
        [`plantProtectionPct-${i}`]: (purchase.plantProtectionPct ?? 0) * 100,
        [`cowFeedPct-${i}`]: (purchase.cowFeedPct ?? 0) * 100,
      });
    });

    formRef.current.validateFields();
  };

  useEffect(() => {
    if (farmPrimaryDataQuery.data && retailersQuery.data && purchasesQuery.data && purchasesQuery.data.length > 0) {
      setPurchasesActive(purchasesQuery.data.length > 0);
      const ps = purchasesQuery.data.map((p): RetailerPurchase => {
        const retailerMatch = retailersQuery.data.find((r) => r.id === p.retailerId) as RetailerData | undefined;

        return {
          id: p.id,
          retailerId: p.retailerId,
          retailer: {
            id: retailerMatch?.id ?? "",
            name: retailerMatch?.name ?? null,
            postcode: retailerMatch?.postcode ?? null,
            countryCode: retailerMatch?.countryCode ?? null,
          },
          mineralFertilizerPct: p.mineralFertilizerPct ?? 0,
          organicFertilizerPct: p.organicFertilizerPct ?? 0,
          plantProtectionPct: p.plantProtectionPct ?? 0,
          seedsPct: p.seedsPct ?? 0,
          cowFeedPct: p.cowFeedPct ?? (carbonProgramQuery.data?.platformFeatures.footprinting?.dairy ? 0 : null),
        };
      });

      setPurchases(ps);
      hardSetFormValues(ps);
    }
  }, [setPurchases, carbonProgramQuery.data?.platformFeatures.footprinting?.dairy, farmPrimaryDataQuery.data, retailersQuery.data, purchasesQuery.data]);

  const updateRetailerField = (index: number, field: keyof RetailerData, value: string | number) => {
    const updatedPurchases = [...purchases];
    updatedPurchases[index] = {
      ...updatedPurchases[index],
      retailer: {
        ...updatedPurchases[index].retailer,
        [field]: value,
      },
    };
    setPurchases(updatedPurchases);
  };

  const updatePurchaseField = (index: number, field: keyof Omit<RetailerPurchase, "retailer" | "id">, value: number | null) => {
    const updatedPurchases = [...purchases];
    updatedPurchases[index] = {
      ...updatedPurchases[index],
      [field]: value,
    };
    setPurchases(updatedPurchases);
  };

  const addPurchase = () => {
    if (!primaryDataId) return;

    const newPurchaes = [
      ...purchases,
      {
        retailer: {
          name: null,
          postcode: null,
          countryCode: null,
        },
        mineralFertilizerPct: null,
        organicFertilizerPct: null,
        plantProtectionPct: null,
        seedsPct: null,
        cowFeedPct: null,
      },
    ];
    setPurchases(newPurchaes);
    hardSetFormValues(newPurchaes);
  };

  const deletePurchase = (index: number) => {
    if (purchases.length <= 1) return;

    const updatedPurchases = [...purchases];
    updatedPurchases.splice(index, 1);
    setPurchases(updatedPurchases);
    hardSetFormValues(updatedPurchases);
  };

  const isLoading = [retailersQuery, purchasesQuery, farmPrimaryDataQuery].some(queryIsLoading);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateFarmResourcePurchases();
        mutateFarmPrimaryData();
      }
    },
    [isLoading, mutateFarmPrimaryData, mutateFarmResourcePurchases, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("farmTransportForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("farmTransportForm.info")}</p>
        </Flex>
      </Card>

      <Card dataTestId="footprinting-farm-transport-form" className="margin-bottom-100 padding-200 padding-top-100">
        <Form ref={formRef}>
          <Flex align="center" gap={16} justify="space-between" className="padding-50">
            <span style={{ maxWidth: "60%" }}>
              {t("farmTransportForm.question.radioQuestion")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("farmTransportForm.tooltips.radioQuestion")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              required
              dataTestId="footprinting-farm-transport-form-radio"
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setPurchasesActive(target.value);
                if (target.value === true && purchases.length === 0) {
                  addPurchase();
                }
              }}
              value={purchasesActive}
              defaultValue={purchasesActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {purchasesActive &&
            purchases.map((purchase, index) => (
              <Space key={purchase.id || index} direction="vertical" size="large" className="padding-top-100">
                <Row>
                  <Col xs={24} sm={22}>
                    <h4>{t("farmTransportForm.retailer", { count: index + 1 })}</h4>
                  </Col>
                  <Col xs={4} sm={2}>
                    {purchases.length > 1 && <Icon iconName="tabler/trash" onClick={() => deletePurchase(index)} className="cursor" />}
                  </Col>
                </Row>
                <Form.Item>
                  <Input
                    className="floating-label margin-bottom-50"
                    dataTestId={`footprinting-farm-transport-form-name-input-${index}`}
                    disabled={readOnly}
                    name={`retailer.name-${index}`}
                    label={t("farmTransportForm.question.retailerNameQuestionLabel")}
                    tooltip={t("farmTransportForm.tooltips.retailerNameQuestionLabel")}
                    value={purchase.retailer.name ?? ""}
                    initialValue={purchase.retailer.name ?? ""}
                    onChange={(e) => updateRetailerField(index, "name", e.target.value)}
                    message={t("farmTransportForm.error.retailerNameQuestionError")}
                    rules={[
                      { required: true },
                      {
                        validator: (_: unknown, value: string) =>
                          value ? Promise.resolve() : Promise.reject(new Error(t("farmTransportForm.error.retailerNameQuestionError"))),
                      },
                      {
                        validator: (_: unknown, value: string) =>
                          isUniqueRetailerName(purchases, value, index) ? Promise.resolve() : Promise.reject(new Error("Retailer name must be unique")),
                      },
                    ]}
                  />
                </Form.Item>
                <div>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    className="floating-label"
                    disabled={readOnly}
                    dataTestId={`footprinting-farm-transport-form-country-select-${index}`}
                    name={`retailer.countryCode-${index}`}
                    label={t("farmTransportForm.question.countryQuestionLabel")}
                    tooltip={t("farmTransportForm.tooltips.countryQuestionLabel")}
                    value={purchase.retailer.countryCode}
                    initialValue={purchase.retailer.countryCode}
                    onChange={(value) => updateRetailerField(index, "countryCode", value || null)}
                    allowClear
                    rules={[{ required: true, message: t("farmTransportForm.error.countryQuestionError") }]}
                    dropdownStyle={{ zIndex: 10010 }}
                  >
                    {Object.entries(TRANSPORT_COUNTRY_CODE).map(([name, code]) => (
                      <Option disabled={readOnly} key={`country-select-${index}-${code}`} label={t(name)} value={code}>
                        {t(`countries.${code}`)}
                      </Option>
                    ))}
                  </Select>
                </div>
                <Form.Item>
                  <Input
                    className="floating-label"
                    dataTestId={`footprinting-farm-transport-form-postcode-input-${index}`}
                    name={`retailer.postcode-${index}`}
                    disabled={readOnly}
                    label={t("farmTransportForm.question.postcodeQuestionLabel")}
                    tooltip={t("farmTransportForm.tooltips.postcodeQuestionLabel")}
                    value={purchase.retailer.postcode ?? ""}
                    initialValue={purchase.retailer.postcode ?? ""}
                    onChange={(e) => updateRetailerField(index, "postcode", e.target.value)}
                    rules={[
                      {
                        validator: (_: unknown, value: string) => {
                          if (!value || value.trim() === "") {
                            return Promise.reject(new Error(t("farmTransportForm.error.postcodeQuestionError")));
                          }

                          if (!isValidPostCode(value, purchase.retailer.countryCode ?? "")) {
                            return Promise.reject(new Error(t("farmTransportForm.error.invalidPostcodeForCountryError")));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>

                <p className="paragraph-m-bold">{t("farmTransportForm.question.general")}</p>

                <Row gutter={0}>
                  <Col xs={24} sm={12}>
                    <Form.Item className="padding-right-50">
                      <InputNumber
                        className="floating-label"
                        dataTestId={`footprinting-farm-transport-form-mineral-fert-input-${index}`}
                        disabled={readOnly}
                        name={`mineralFertilizerPct-${index}`}
                        label={`${t("farmTransportForm.question.mineralFertilizerQuestionLabel")}*`}
                        tooltip={t("farmTransportForm.tooltips.mineralFertilizerQuestionLabel")}
                        suffix="%"
                        value={formatFractionInput(purchase.mineralFertilizerPct)}
                        initialValue={formatFractionInput(purchase.mineralFertilizerPct)}
                        onChange={(v) => updatePurchaseField(index, "mineralFertilizerPct", formatPercentageInput(v))}
                        precision={2}
                        validateFirst
                        rules={[
                          { type: "number", message: t("farmTransportForm.error.percentageValue") },
                          {
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.explicitZeroRequired")));
                              }

                              if (!isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.percentageValue")));
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item className="padding-left-50">
                      <InputNumber
                        className="floating-label"
                        dataTestId={`footprinting-farm-transport-form-organic-fert-input-${index}`}
                        disabled={readOnly}
                        name={`organicFertilizerPct-${index}`}
                        label={`${t("farmTransportForm.question.organicFertilizerQuestionLabel")}*`}
                        tooltip={t("farmTransportForm.tooltips.organicFertilizerQuestionLabel")}
                        suffix="%"
                        value={formatFractionInput(purchase.organicFertilizerPct)}
                        initialValue={formatFractionInput(purchase.organicFertilizerPct)}
                        onChange={(v) => updatePurchaseField(index, "organicFertilizerPct", formatPercentageInput(v))}
                        precision={2}
                        rules={[
                          { type: "number", message: t("farmTransportForm.error.percentageValue") },
                          {
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.explicitZeroRequired")));
                              }

                              if (!isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.percentageValue")));
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={0} className="margin-bottom-100 padding-top-100">
                  <Col xs={24} sm={12}>
                    <Form.Item className="padding-right-50">
                      <InputNumber
                        className="floating-label"
                        dataTestId={`footprinting-farm-transport-form-seeds-input-${index}`}
                        disabled={readOnly}
                        name={`seedsPct-${index}`}
                        label={`${t("farmTransportForm.question.seedsQuestionLabel")}*`}
                        tooltip={t("farmTransportForm.tooltips.seedsQuestionLabel")}
                        suffix="%"
                        value={formatFractionInput(purchase.seedsPct)}
                        initialValue={formatFractionInput(purchase.seedsPct)}
                        onChange={(v) => updatePurchaseField(index, "seedsPct", formatPercentageInput(v))}
                        precision={2}
                        rules={[
                          { type: "number", message: t("farmTransportForm.error.percentageValue") },
                          {
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.explicitZeroRequired")));
                              }

                              if (!isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.percentageValue")));
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item className="padding-left-50">
                      <InputNumber
                        className="floating-label"
                        dataTestId={`footprinting-farm-transport-form-plant-protection-input-${index}`}
                        disabled={readOnly}
                        name={`plantProtectionPct-${index}`}
                        label={`${t("farmTransportForm.question.plantProtectionQuestionLabel")}*`}
                        tooltip={t("farmTransportForm.tooltips.plantProtectionQuestionLabel")}
                        suffix="%"
                        value={formatFractionInput(purchase.plantProtectionPct)}
                        initialValue={formatFractionInput(purchase.plantProtectionPct)}
                        onChange={(v) => updatePurchaseField(index, "plantProtectionPct", formatPercentageInput(v))}
                        precision={2}
                        rules={[
                          { type: "number", message: t("farmTransportForm.error.percentageValue") },
                          {
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.explicitZeroRequired")));
                              }

                              if (!isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("farmTransportForm.error.percentageValue")));
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {carbonProgramQuery.data?.platformFeatures?.footprinting?.dairy === true ? (
                  <Row gutter={0} className="margin-bottom-100">
                    <Col xs={24} sm={12}>
                      <Form.Item className="padding-right-50">
                        <InputNumber
                          className="floating-label"
                          dataTestId={`footprinting-farm-transport-form-seeds-input-${index}`}
                          disabled={readOnly}
                          name={`cowFeedPct-${index}`}
                          label={`${t("farmTransportForm.question.cowFeedQuestionLabel")}*`}
                          tooltip={t("farmTransportForm.tooltips.cowFeedQuestionLabel")}
                          suffix="%"
                          value={formatFractionInput(purchase.cowFeedPct)}
                          initialValue={formatFractionInput(purchase.cowFeedPct)}
                          onChange={(v) => updatePurchaseField(index, "cowFeedPct", formatPercentageInput(v))}
                          precision={2}
                          rules={[
                            { type: "number", message: t("farmTransportForm.error.percentageValue") },
                            {
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v)) {
                                  return Promise.reject(new Error(t("farmTransportForm.error.explicitZeroRequired")));
                                }

                                if (!isValidPercentageDecimal(v)) {
                                  return Promise.reject(new Error(t("farmTransportForm.error.percentageValue")));
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : null}

                {index === purchases.length - 1 && (
                  <div>
                    <Button type="text" className="padding-left-0 margin-top-0" disabled={readOnly} onClick={addPurchase}>
                      <Icon iconName="klim/plus" className="icon inline" />
                      {t("farmTransportForm.add")}
                    </Button>
                  </div>
                )}
              </Space>
            ))}
        </Form>
      </Card>
    </div>
  );
};
